import React from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Crown } from 'lucide-react';
const WHATSAPP_LINK = 'https://wa.me/message/TNDFTC7M6U4AI1';
type Plan = {
  name: string;
  price: string;
  accompaniment: string;
  description: string;
  bullets: string[];
  buttonText: string;
  featured?: boolean;
  badge?: string;
};
const plans: Plan[] = [{
  name: 'PLAN PREMIUM',
  price: '300',
  accompaniment: 'Curso asincrónico',
  description: 'Ideal para quienes quieren empezar con una base clara, ordenada y accionable para construir su tienda online.',
  bullets: ['Acceso por 6 meses', 'Ideal si querés avanzar a tu ritmo', 'Prompts y automatizaciones con IA para tu negocio'],
  buttonText: 'Consultar Plan Premium'
}, {
  name: 'PLAN ELITE',
  price: '490',
  accompaniment: '1 mes de acompañamiento',
  description: 'Para quienes quieren avanzar con más guía, corrección y acompañamiento en el proceso.',
  bullets: ['Todo lo incluido en la opción 1.', 'Acompañamiento directo por WhatsApp durante 1 mes', 'Feedback, análisis de métricas, correcciones de creativos y landings.', 'Audios y consultas ilimitadas'],
  buttonText: 'Consultar Plan Elite'
}, {
  name: 'PLAN BUSINESS',
  price: '720',
  accompaniment: '3 meses de acompañamiento',
  description: 'La opción más completa para quienes quieren trabajar de forma más cercana y construir su e-commerce con mayor nivel de seguimiento.',
  bullets: ['Todo lo incluido en las opciones anteriores.', 'Acompañamiento 100% personalizado por WhatsApp durante 3 meses.', 'Llamadas estratégicas 1:1 según necesidad', 'Análisis de métricas, feedback continuo y escalado.'],
  buttonText: 'Consultar Plan Business',
  featured: true,
  badge: 'Más completo'
}];
function PlanCard({
  plan,
  index
}: {
  plan: Plan;
  index: number;
}) {
  const featured = plan.featured;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className={`relative rounded-3xl flex flex-col h-full ${featured ? 'bg-gradient-to-b from-brand-card to-brand-dark border-2 border-brand-gold p-8 md:p-10 shadow-[0_0_50px_rgba(212,175,55,0.25)] lg:scale-[1.06] lg:z-10' : 'bg-brand-card border border-brand-border p-8 lg:mt-6'}`} data-id="element-49">
      {plan.badge && <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-brand-gold text-brand-darker px-5 py-1.5 rounded-full text-sm font-bold tracking-wide whitespace-nowrap shadow-lg" data-id="element-50">
          <Crown className="w-4 h-4" data-id="element-51" />
          {plan.badge}
        </div>}

      <div className="mb-6" data-id="element-52">
        <h3 className={`font-bold text-brand-goldLight mb-4 tracking-wider ${featured ? 'text-2xl' : 'text-xl'}`} data-id="element-53">
          {plan.name}
        </h3>
        <div className="flex items-baseline gap-1 mb-4" data-id="element-54">
          <span className="text-2xl text-brand-muted" data-id="element-55">USD</span>
          <span className={`font-bold ${featured ? 'text-6xl md:text-7xl' : 'text-5xl'}`} data-id="element-56">
            {plan.price}
          </span>
        </div>

        {/* Acompañamiento destacado — clave diferenciadora entre planes */}
        <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold mb-4 ${featured ? 'bg-brand-gold text-brand-darker' : 'bg-brand-gold/10 text-brand-goldLight border border-brand-gold/25'}`} data-id="element-57">
          <Clock className="w-4 h-4" data-id="element-58" />
          {plan.accompaniment}
        </div>

        <p className={`text-brand-muted ${featured ? 'text-base' : 'text-sm'} min-h-[60px]`} data-id="element-59">
          {plan.description}
        </p>
      </div>

      <div className="space-y-4 mb-8 flex-grow" data-id="element-60">
        {plan.bullets.map((bullet, j) => <div key={j} className="flex items-start gap-3" data-id="element-61">
            <Check className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-62" />
            <span className={`text-brand-text/90 ${featured ? 'text-base' : 'text-sm'}`} data-id="element-63">
              {bullet}
            </span>
          </div>)}
      </div>

      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className={`w-full rounded-xl font-bold text-center transition-all ${featured ? 'py-5 text-lg bg-brand-gold text-brand-darker hover:bg-brand-goldLight shadow-[0_0_25px_rgba(212,175,55,0.35)]' : 'py-4 bg-brand-dark border border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10'}`} data-id="element-64">
        {plan.buttonText}
      </a>
    </motion.div>;
}
export function Pricing() {
  return <section className="py-24 px-6 relative" data-id="element-65">
      <div className="max-w-7xl mx-auto" data-id="element-66">
        <div className="text-center mb-20" data-id="element-67">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-68">
            Opciones de ingreso
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-brand-muted max-w-2xl mx-auto" data-id="element-69">
            Elegí el nivel de acompañamiento que mejor se adapte a tu momento y
            empezá a construir tu e-commerce con una estructura clara.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 mb-12 items-stretch" data-id="element-70">
          {plans.map((plan, i) => <PlanCard key={i} plan={plan} index={i} data-id="element-71" />)}
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center space-y-2 text-brand-muted" data-id="element-72">
          <p data-id="element-73">Consultar descuentos por pagos con transferencia.</p>
          <p data-id="element-74">Opciones de pago disponibles. Consultar por WhatsApp.</p>
        </motion.div>
      </div>
    </section>;
}